import React, { useState } from 'react';
import { CheckCircle2, Edit, Users, Shield, Mail, ChevronDown, ChevronUp, AlertTriangle } from 'lucide-react';

export default function ConfigurationReviewStep({ wizardData, onComplete }) {
  const [expandedSections, setExpandedSections] = useState({
    roles: true,
    permissions: true,
    teamMembers: true
  });
  const [confirmationChecked, setConfirmationChecked] = useState(false);

  // Toggle section expansion
  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev?.[section]
    }));
  };

  // Get role display name
  const getRoleDisplayName = (roleId) => {
    const role = wizardData?.roles?.find(r => r?.tempId === roleId || r?.id === roleId);
    return role?.displayName || 'Unknown Role';
  };

  // Get permission count for role
  const getPermissionCount = (roleId) => {
    const permissions = wizardData?.permissions?.[roleId] || {};
    return Object.values(permissions)?.filter(level => level !== 'none')?.length;
  };

  // Group team members by role
  const membersByRole = wizardData?.teamMembers?.reduce((acc, member) => {
    const roleName = getRoleDisplayName(member?.roleId);
    if (!acc?.[roleName]) {
      acc[roleName] = [];
    }
    acc?.[roleName]?.push(member);
    return acc;
  }, {});

  // Validation warnings
  const warnings = [];
  if (wizardData?.roles?.length === 0) {
    warnings?.push('No roles have been created');
  }
  if (Object.keys(wizardData?.permissions)?.length === 0) {
    warnings?.push('No permissions have been assigned');
  }
  if (wizardData?.teamMembers?.length === 0) {
    warnings?.push('No team members have been invited');
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold text-gray-900">Review Configuration</h2>
        <p className="mt-2 text-gray-600">
          Review your setup configuration before completing the initial setup. You can edit any section if needed.
        </p>
      </div>
      {/* Warnings */}
      {warnings?.length > 0 && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-start">
            <AlertTriangle className="w-5 h-5 text-yellow-600 mr-3 mt-0.5" />
            <div className="flex-1">
              <h3 className="text-sm font-medium text-yellow-800">Configuration Warnings</h3>
              <ul className="mt-2 text-sm text-yellow-700 list-disc list-inside">
                {warnings?.map((warning, idx) => (
                  <li key={idx}>{warning}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}
      {/* Configuration Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Roles Summary */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
          <div className="flex items-center justify-between mb-2">
            <Shield className="w-8 h-8 text-blue-600" />
            <span className="text-3xl font-bold text-blue-900">{wizardData?.roles?.length}</span>
          </div>
          <h3 className="text-lg font-semibold text-blue-900">Roles Created</h3>
          <p className="text-sm text-blue-700 mt-1">Business roles configured</p>
        </div>

        {/* Permissions Summary */}
        <div className="bg-green-50 border border-green-200 rounded-lg p-6">
          <div className="flex items-center justify-between mb-2">
            <CheckCircle2 className="w-8 h-8 text-green-600" />
            <span className="text-3xl font-bold text-green-900">
              {wizardData?.roles?.reduce((sum, role) => sum + getPermissionCount(role?.tempId), 0)}
            </span>
          </div>
          <h3 className="text-lg font-semibold text-green-900">Permissions Assigned</h3>
          <p className="text-sm text-green-700 mt-1">Total permission grants</p>
        </div>

        {/* Team Members Summary */}
        <div className="bg-purple-50 border border-purple-200 rounded-lg p-6">
          <div className="flex items-center justify-between mb-2">
            <Users className="w-8 h-8 text-purple-600" />
            <span className="text-3xl font-bold text-purple-900">{wizardData?.teamMembers?.length}</span>
          </div>
          <h3 className="text-lg font-semibold text-purple-900">Team Members</h3>
          <p className="text-sm text-purple-700 mt-1">Users to be invited</p>
        </div>
      </div>
      {/* Detailed Review Sections */}
      <div className="space-y-4">
        {/* Roles Review */}
        <div className="border border-gray-200 rounded-lg overflow-hidden">
          <div
            className="bg-gray-50 px-6 py-4 flex items-center justify-between cursor-pointer hover:bg-gray-100"
            onClick={() => toggleSection('roles')}
          >
            <div className="flex items-center space-x-3">
              <Shield className="w-5 h-5 text-gray-600" />
              <h3 className="font-semibold text-gray-900">
                Roles Configuration ({wizardData?.roles?.length})
              </h3>
            </div>
            {expandedSections?.roles ? (
              <ChevronUp className="w-5 h-5 text-gray-400" />
            ) : (
              <ChevronDown className="w-5 h-5 text-gray-400" />
            )}
          </div>

          {expandedSections?.roles && (
            <div className="p-6 bg-white">
              {wizardData?.roles?.length > 0 ? (
                <div className="space-y-3">
                  {wizardData?.roles?.map(role => (
                    <div key={role?.tempId} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                      <div>
                        <h4 className="font-medium text-gray-900">{role?.displayName}</h4>
                        <p className="text-sm text-gray-600">{role?.description || 'No description'}</p>
                        <p className="text-xs text-gray-500 mt-1">
                          Hierarchy Level: {role?.hierarchyLevel} • {getPermissionCount(role?.tempId)} permissions
                        </p>
                      </div>
                      <button className="text-blue-600 hover:text-blue-800 text-sm font-medium flex items-center">
                        <Edit className="w-4 h-4 mr-1" />
                        Edit
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-center py-4">No roles configured</p>
              )}
            </div>
          )}
        </div>

        {/* Permissions Review */}
        <div className="border border-gray-200 rounded-lg overflow-hidden">
          <div
            className="bg-gray-50 px-6 py-4 flex items-center justify-between cursor-pointer hover:bg-gray-100"
            onClick={() => toggleSection('permissions')}
          >
            <div className="flex items-center space-x-3">
              <CheckCircle2 className="w-5 h-5 text-gray-600" />
              <h3 className="font-semibold text-gray-900">
                Permissions Configuration
              </h3>
            </div>
            {expandedSections?.permissions ? (
              <ChevronUp className="w-5 h-5 text-gray-400" />
            ) : (
              <ChevronDown className="w-5 h-5 text-gray-400" />
            )}
          </div>

          {expandedSections?.permissions && (
            <div className="p-6 bg-white">
              {wizardData?.roles?.length > 0 ? (
                <div className="space-y-4">
                  {wizardData?.roles?.map(role => {
                    const permissions = wizardData?.permissions?.[role?.tempId] || {};
                    const permissionCount = Object.values(permissions)?.filter(l => l !== 'none')?.length;
                    
                    return (
                      <div key={role?.tempId} className="border-l-4 border-blue-500 pl-4">
                        <h4 className="font-medium text-gray-900 mb-2">
                          {role?.displayName} 
                          <span className="text-sm text-gray-500 ml-2">
                            ({permissionCount} permissions)
                          </span>
                        </h4>
                        {permissionCount > 0 ? (
                          <div className="flex flex-wrap gap-2">
                            {Object.entries(permissions)?.filter(([_, level]) => level !== 'none')?.map(([feature, level]) => (
                                <span
                                  key={feature}
                                  className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                                >
                                  {feature}: {level}
                                </span>
                              ))
                            }
                          </div>
                        ) : (
                          <p className="text-sm text-gray-500">No permissions assigned</p>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-gray-500 text-center py-4">No permissions configured</p>
              )}
            </div>
          )}
        </div>

        {/* Team Members Review */}
        <div className="border border-gray-200 rounded-lg overflow-hidden">
          <div
            className="bg-gray-50 px-6 py-4 flex items-center justify-between cursor-pointer hover:bg-gray-100"
            onClick={() => toggleSection('teamMembers')}
          >
            <div className="flex items-center space-x-3">
              <Users className="w-5 h-5 text-gray-600" />
              <h3 className="font-semibold text-gray-900">
                Team Members ({wizardData?.teamMembers?.length})
              </h3>
            </div>
            {expandedSections?.teamMembers ? (
              <ChevronUp className="w-5 h-5 text-gray-400" />
            ) : (
              <ChevronDown className="w-5 h-5 text-gray-400" />
            )}
          </div>

          {expandedSections?.teamMembers && (
            <div className="p-6 bg-white">
              {wizardData?.teamMembers?.length > 0 ? (
                <div className="space-y-4">
                  {Object.entries(membersByRole)?.map(([roleName, members]) => (
                    <div key={roleName}>
                      <h4 className="font-medium text-gray-700 mb-2 flex items-center">
                        {roleName} ({members?.length})
                      </h4>
                      <div className="space-y-2 pl-4">
                        {members?.map(member => (
                          <div key={member?.id} className="flex items-center space-x-3 text-sm">
                            <Mail className="w-4 h-4 text-gray-400" />
                            <span className="text-gray-900">{member?.email}</span>
                            {(member?.firstName || member?.lastName) && (
                              <span className="text-gray-500">
                                • {`${member?.firstName} ${member?.lastName}`?.trim()}
                              </span>
                            )}
                            {member?.department && (
                              <span className="text-gray-500">• {member?.department}</span>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-center py-4">No team members added</p>
              )}
            </div>
          )}
        </div>
      </div>
      {/* Final Confirmation */}
      <div className="bg-gradient-to-r from-blue-50 to-purple-50 border border-blue-200 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Ready to Complete Setup?</h3>
        
        <div className="space-y-3 mb-6">
          <div className="flex items-start">
            <CheckCircle2 className="w-5 h-5 text-green-600 mr-3 mt-0.5" />
            <div>
              <p className="font-medium text-gray-900">Roles will be created</p>
              <p className="text-sm text-gray-600">All configured roles will be added to your CRM system</p>
            </div>
          </div>
          
          <div className="flex items-start">
            <CheckCircle2 className="w-5 h-5 text-green-600 mr-3 mt-0.5" />
            <div>
              <p className="font-medium text-gray-900">Permissions will be assigned</p>
              <p className="text-sm text-gray-600">Access levels will be configured for each role</p>
            </div>
          </div>
          
          <div className="flex items-start">
            <CheckCircle2 className="w-5 h-5 text-green-600 mr-3 mt-0.5" />
            <div>
              <p className="font-medium text-gray-900">Invitations will be sent</p>
              <p className="text-sm text-gray-600">Team members will receive email invitations to join</p>
            </div>
          </div>
        </div>

        <label className="flex items-start cursor-pointer">
          <input
            type="checkbox"
            checked={confirmationChecked}
            onChange={(e) => setConfirmationChecked(e?.target?.checked)}
            className="mt-1 w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
          />
          <span className="ml-3 text-sm text-gray-700">
            I confirm that I have reviewed the configuration above and I'm ready to complete the initial setup. 
            I understand that invitation emails will be sent to all team members.
          </span>
        </label>

        {!confirmationChecked && (
          <p className="mt-4 text-sm text-orange-600 flex items-center">
            <AlertTriangle className="w-4 h-4 mr-2" />
            Please check the confirmation box above to proceed
          </p>
        )}
      </div>
      {/* Help Section */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h4 className="font-semibold text-blue-900 mb-2">🎉 What Happens Next?</h4>
        <ul className="text-sm text-blue-800 space-y-1 list-disc list-inside">
          <li>Your CRM will be configured with the roles and permissions you've defined</li>
          <li>Team members will receive email invitations within a few minutes</li>
          <li>You can manage roles, permissions, and users anytime from Settings → User Management</li>
          <li>Your team can start using the CRM as soon as they accept their invitations</li>
        </ul>
      </div>
    </div>
  );
}